import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Bell, Calculator, CheckCheck, Cpu, Globe2, Loader2, Network, Plus, Radar, Route, ScanSearch, Trash2, Wrench, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { core, apiError } from '@/shared/api/client';
import { useGet } from '@/shared/api/hooks';
import { can, useAuth } from '@/shared/lib/auth';
import { cn } from '@/shared/lib/utils';
import { ago, dt, n0 } from '@/shared/lib/format';
import { Dialog, DialogContent, DialogTitle } from '@/shared/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { getLanguage, useT } from '@/i18n';

/** An event's title in the reader's language: NMM / ACS events carry their texts in every language (params.texts). */
const evTitle = (t: ReturnType<typeof useT>, e: { title: string; params?: any }) => String(e.params?.texts?.[getLanguage()]?.title || t(e.title, { ...e.params, defaultValue: e.title }));

/* ───────────────────────── small shared bits ───────────────────────── */
const field = 'h-9 w-full rounded-lg border bg-background px-3 text-sm outline-none focus:border-primary';
function Btn({ busy, children, ...p }: React.ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean }) {
  return <button {...p} disabled={busy || p.disabled} className={cn('inline-flex h-9 items-center gap-1.5 rounded-lg bg-gradient-to-r from-[var(--nx-brand-from)] to-[var(--nx-brand-to)] px-4 text-sm font-semibold text-white shadow-sm shadow-blue-600/30 disabled:opacity-60', p.className)}>{busy && <Loader2 className="size-4 animate-spin" />}{children}</button>;
}
const Stat = ({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) => (
  <div className="rounded-xl border bg-muted/30 px-3 py-2"><div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div><div className={cn('text-lg font-bold tabular-nums', tone)}>{value}</div></div>
);
const IconBtn = ({ children, label, badge, onClick, active }: { children: ReactNode; label: string; badge?: number; onClick?: () => void; active?: boolean }) => (
  <button title={label} onClick={onClick} className={cn('relative flex size-9 items-center justify-center rounded-lg border bg-background text-muted-foreground transition hover:border-primary/40 hover:text-primary', active && 'border-primary/50 text-primary')}>
    {children}
    {!!badge && <span className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9.5px] font-bold text-white ring-2 ring-card">{badge > 99 ? '99+' : badge}</span>}
  </button>
);

/* ───────────────────────── Tools menu ───────────────────────── */
type Tool = 'ping' | 'traceroute' | 'dns' | 'mac' | 'session' | 'subnet' | 'budget';
const TOOLS: { key: Tool; icon: typeof Zap; tint: string; caps?: string[] }[] = [
  { key: 'ping', icon: Zap, tint: 'from-sky-500 to-blue-600', caps: ['nmm.view', 'aaa.view', 'acs.view'] },
  { key: 'traceroute', icon: Route, tint: 'from-violet-500 to-fuchsia-600', caps: ['nmm.view', 'aaa.view', 'acs.view'] },
  { key: 'dns', icon: Globe2, tint: 'from-emerald-500 to-teal-600', caps: ['nmm.view', 'aaa.view', 'acs.view'] },
  { key: 'session', icon: ScanSearch, tint: 'from-amber-500 to-orange-600', caps: ['aaa.view', 'crm.view', 'nmm.view'] },
  { key: 'mac', icon: Cpu, tint: 'from-rose-500 to-pink-600', caps: ['nmm.view', 'aaa.view', 'acs.view', 'crm.view'] },
  { key: 'subnet', icon: Network, tint: 'from-cyan-500 to-sky-600' },
  { key: 'budget', icon: Calculator, tint: 'from-lime-500 to-emerald-600' },
];

export function ToolsMenu() {
  const t = useT();
  const user = useAuth((s) => s.user);
  const [open, setOpen] = useState(false);
  const [tool, setTool] = useState<Tool | null>(null);
  const list = TOOLS.filter((x) => !x.caps || can(user, ...x.caps));
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild><span><IconBtn label={t('tools.title')} active={open}><Wrench className="size-4" /></IconBtn></span></PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-2">
          <div className="px-2 pb-2 pt-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{t('tools.title')}</div>
          <div className="grid grid-cols-2 gap-1.5">
            {list.map((x) => (
              <button key={x.key} onClick={() => { setTool(x.key); setOpen(false); }} className="group flex items-center gap-2 rounded-xl border bg-card p-2 text-start text-[12.5px] font-medium transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
                <span className={cn('flex size-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-sm', x.tint)}><x.icon className="size-3.5" /></span>
                <span className="leading-tight">{t(`tools.${x.key}`)}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <Dialog open={!!tool} onOpenChange={(o) => !o && setTool(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
          {tool && <DialogTitle className="flex items-center gap-2 text-base">{(() => { const x = TOOLS.find((y) => y.key === tool)!; return <span className={cn('flex size-7 items-center justify-center rounded-lg bg-gradient-to-br text-white', x.tint)}><x.icon className="size-3.5" /></span>; })()}{t(`tools.${tool}`)}</DialogTitle>}
          {tool === 'ping' && <PingTool />}
          {tool === 'traceroute' && <TraceTool />}
          {tool === 'dns' && <DnsTool />}
          {tool === 'mac' && <MacTool />}
          {tool === 'session' && <SessionTool />}
          {tool === 'subnet' && <SubnetTool />}
          {tool === 'budget' && <BudgetTool />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function useRun<T>(path: string) {
  const [busy, setBusy] = useState(false); const [res, setRes] = useState<T | null>(null);
  const run = async (body: unknown) => { setBusy(true); try { setRes((await core.post(path, body)).data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  return { busy, res, run };
}

function PingTool() {
  const t = useT(); const [host, setHost] = useState(''); const [count, setCount] = useState(5);
  const { busy, res, run } = useRun<any>('/tools/ping');
  const maxMs = Math.max(1, ...(res?.replies ?? []).map((r: any) => r.ms));
  return (
    <div className="space-y-4">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); run({ host, count }); }}>
        <input className={field} value={host} onChange={(e) => setHost(e.target.value)} placeholder={t('tools.host')} required autoFocus />
        <select className={cn(field, 'w-24')} value={count} onChange={(e) => setCount(Number(e.target.value))}>{[3, 5, 10, 20].map((n) => <option key={n} value={n}>{n}</option>)}</select>
        <Btn busy={busy}>{busy ? t('tools.running') : t('tools.run')}</Btn>
      </form>
      {res && (
        <>
          <div className="grid grid-cols-4 gap-2">
            <Stat label={t('tools.loss')} value={`${res.lossPct}%`} tone={res.lossPct >= 100 ? 'text-rose-600' : res.lossPct > 0 ? 'text-amber-600' : 'text-emerald-600'} />
            <Stat label={t('tools.min')} value={res.min ?? '—'} /><Stat label={t('tools.avg')} value={res.avg ?? '—'} /><Stat label={t('tools.max')} value={res.max ?? '—'} />
          </div>
          <div className="flex h-24 items-end gap-1 rounded-xl border bg-muted/20 p-2">
            {res.replies.map((r: any) => <div key={r.seq} title={`${t('tools.reply', { seq: r.seq })}: ${r.ms} ms`} className="flex-1 rounded-t bg-gradient-to-t from-blue-600 to-cyan-400" style={{ height: `${Math.max(6, (r.ms / maxMs) * 100)}%` }} />)}
            {!res.replies.length && <div className="m-auto text-sm text-rose-600">{t('tools.noReply')}</div>}
          </div>
        </>
      )}
    </div>
  );
}

function TraceTool() {
  const t = useT(); const [host, setHost] = useState('');
  const { busy, res, run } = useRun<any>('/tools/traceroute');
  return (
    <div className="space-y-4">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); run({ host }); }}>
        <input className={field} value={host} onChange={(e) => setHost(e.target.value)} placeholder={t('tools.host')} required autoFocus /><Btn busy={busy}>{busy ? t('tools.running') : t('tools.run')}</Btn>
      </form>
      {res && (
        <ol className="relative space-y-1.5 border-s-2 border-dashed border-primary/30 ps-5">
          {res.hops.map((h: any) => (
            <li key={h.hop} className="relative flex items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm">
              <span className="absolute -start-[29px] flex size-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">{h.hop}</span>
              <span className="font-mono">{h.ip ?? '* * *'}</span><span className="ms-auto tabular-nums text-muted-foreground">{h.ms != null ? `${h.ms} ms` : ''}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function DnsTool() {
  const t = useT(); const [name, setName] = useState(''); const [type, setType] = useState('A'); const [server, setServer] = useState('');
  const { busy, res, run } = useRun<any>('/tools/dns');
  return (
    <div className="space-y-4">
      <form className="grid grid-cols-[1fr_110px] gap-2" onSubmit={(e) => { e.preventDefault(); run({ name, type, server: server || undefined }); }}>
        <input className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('tools.name')} required autoFocus />
        <select className={field} value={type} onChange={(e) => setType(e.target.value)}>{['A', 'AAAA', 'MX', 'TXT', 'NS', 'CNAME', 'PTR'].map((x) => <option key={x}>{x}</option>)}</select>
        <input className={field} value={server} onChange={(e) => setServer(e.target.value)} placeholder={t('tools.server')} /><Btn busy={busy}>{t('tools.run')}</Btn>
      </form>
      {res && (
        <div className="rounded-xl border bg-muted/20 p-3 text-sm">
          <div className="mb-2 text-xs text-muted-foreground">{res.server} · {res.ms} ms</div>
          {res.records?.length ? <ul className="space-y-1 font-mono text-[13px]">{res.records.map((r: any, i: number) => <li key={i}>{typeof r === 'string' ? r : Array.isArray(r) ? r.join('') : JSON.stringify(r)}</li>)}</ul>
            : <div className="text-amber-700">{t('tools.noRecords', { error: res.error ?? '—' })}</div>}
        </div>
      )}
    </div>
  );
}

function MacTool() {
  const t = useT(); const [mac, setMac] = useState('');
  const { busy, res, run } = useRun<any>('/tools/mac');
  return (
    <div className="space-y-4">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); run({ mac }); }}><input className={cn(field, 'font-mono')} value={mac} onChange={(e) => setMac(e.target.value)} placeholder={t('tools.macInput')} required autoFocus /><Btn busy={busy}>{t('tools.run')}</Btn></form>
      {res && (
        <div className="space-y-3">
          <div className="rounded-xl border bg-gradient-to-br from-rose-500/10 to-pink-500/5 p-4">
            <div className="text-[11px] font-semibold uppercase text-muted-foreground">{t('tools.vendor')} · {res.prefix}</div>
            <div className="text-lg font-bold">{res.vendor ?? t('tools.unknownVendor')}</div>
            {res.address && <div className="text-xs text-muted-foreground">{res.address}</div>}
            {res.locallyAdministered && <div className="mt-1 text-xs font-medium text-amber-700">{t('tools.locallyAdministered')}</div>}
          </div>
          {!!res.seenOn?.length && <SessionList title={t('tools.seenOn')} rows={res.seenOn.map((s: any) => ({ ...s, customer: s.name }))} />}
        </div>
      )}
    </div>
  );
}

function SessionTool() {
  const t = useT(); const [query, setQuery] = useState('');
  const { busy, res, run } = useRun<any>('/tools/session');
  return (
    <div className="space-y-4">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); run({ query }); }}><input className={cn(field, 'font-mono')} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('tools.sessionInput')} required autoFocus /><Btn busy={busy}>{t('tools.run')}</Btn></form>
      {res && (res.sessions.length ? <SessionList rows={res.sessions} /> : <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">{t('tools.noSessions')}</div>)}
    </div>
  );
}
function SessionList({ rows, title }: { rows: any[]; title?: string }) {
  const t = useT(); const nav = useNavigate();
  return (
    <div className="space-y-1.5">
      {title && <div className="text-[11px] font-bold uppercase text-muted-foreground">{title}</div>}
      {rows.map((s, i) => (
        <button key={i} onClick={() => s.customer_id && nav(`/crm/customers/${s.customer_id}`)} className="flex w-full items-center gap-3 rounded-xl border bg-card px-3 py-2 text-start text-sm hover:border-primary/40">
          <span className={cn('size-2.5 rounded-full', s.online ?? !s.acctstoptime ? 'bg-emerald-500 shadow-[0_0_0_3px] shadow-emerald-500/20' : 'bg-slate-400')} />
          <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{s.customer ?? s.username}</span><span className="block truncate font-mono text-[11.5px] text-muted-foreground">{s.username} · {s.ip ?? '—'}{s.mac ? ` · ${s.mac}` : ''}{s.nas ? ` · ${s.nas}` : ''}</span></span>
          <span className="text-end text-[11px] text-muted-foreground">{(s.online ?? !s.acctstoptime) ? t('tools.online') : t('tools.offline')}<br />{dt(s.acctstarttime)}</span>
        </button>
      ))}
    </div>
  );
}

export function subnetInfo(cidr: string) {
  const m = cidr.trim().match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/);
  if (!m) return null;
  const oct = m.slice(1, 5).map(Number); const p = Number(m[5]);
  if (oct.some((o) => o > 255) || p > 32) return null;
  const ip = ((oct[0] << 24) >>> 0) + (oct[1] << 16) + (oct[2] << 8) + oct[3];
  const mask = p === 0 ? 0 : (0xffffffff << (32 - p)) >>> 0;
  const net = (ip & mask) >>> 0; const bc = (net | (~mask >>> 0)) >>> 0;
  const s = (n: number) => [n >>> 24, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
  const usable = p >= 31 ? (p === 32 ? 1 : 2) : Math.max(0, bc - net - 1);
  return { network: s(net), broadcast: s(bc), first: s(p >= 31 ? net : net + 1), last: s(p >= 31 ? bc : bc - 1), hosts: usable, mask: s(mask), wildcard: s(~mask >>> 0), prefix: p };
}
function SubnetTool() {
  const t = useT(); const [v, setV] = useState('100.64.0.0/22');
  const r = useMemo(() => subnetInfo(v), [v]);
  return (
    <div className="space-y-4">
      <input className={cn(field, 'font-mono')} value={v} onChange={(e) => setV(e.target.value)} placeholder={t('tools.cidr')} autoFocus />
      {r && <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Stat label={t('tools.network')} value={<span className="font-mono text-base">{r.network}/{r.prefix}</span>} /><Stat label={t('tools.broadcast')} value={<span className="font-mono text-base">{r.broadcast}</span>} />
        <Stat label={t('tools.hosts')} value={n0(r.hosts)} tone="text-emerald-600" /><Stat label={t('tools.first')} value={<span className="font-mono text-base">{r.first}</span>} />
        <Stat label={t('tools.last')} value={<span className="font-mono text-base">{r.last}</span>} /><Stat label={t('tools.mask')} value={<span className="font-mono text-base">{r.mask}</span>} />
      </div>}
    </div>
  );
}

/** GPON-style link budget: fibre 0.35 dB/km (1490 nm), splitter losses per ITU-T G.671 typical values. */
const SPLIT_LOSS: Record<string, number> = { '1:2': 3.8, '1:4': 7.4, '1:8': 10.7, '1:16': 13.9, '1:32': 17.2, '1:64': 21.0 };
export function linkBudget(i: { tx: number; sens: number; km: number; split: string; conn: number; splices: number }) {
  const fibre = i.km * 0.35; const split = SPLIT_LOSS[i.split] ?? 0; const other = i.conn * 0.5 + i.splices * 0.1;
  const rx = i.tx - fibre - split - other; const margin = rx - i.sens;
  return { fibre: +fibre.toFixed(2), split, other: +other.toFixed(2), rx: +rx.toFixed(2), margin: +margin.toFixed(2), verdict: margin >= 3 ? 'ok' : margin >= 0 ? 'low' : 'fail' };
}
function BudgetTool() {
  const t = useT(); const [s, set] = useState({ tx: 3, sens: -28, km: 10, split: '1:32', conn: 4, splices: 6 });
  const r = linkBudget(s);
  const num = (k: keyof typeof s, label: string, step = 1) => <label className="block space-y-1"><span className="text-[11px] font-semibold text-muted-foreground">{label}</span><input type="number" step={step} className={field} value={s[k] as number} onChange={(e) => set({ ...s, [k]: Number(e.target.value) })} /></label>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {num('tx', t('tools.txPower'), 0.5)}{num('sens', t('tools.rxSens'), 0.5)}{num('km', t('tools.distance'), 0.5)}
        <label className="block space-y-1"><span className="text-[11px] font-semibold text-muted-foreground">{t('tools.splitter')}</span><select className={field} value={s.split} onChange={(e) => set({ ...s, split: e.target.value })}>{Object.keys(SPLIT_LOSS).map((k) => <option key={k}>{k}</option>)}</select></label>
        {num('conn', t('tools.connectors'))}{num('splices', t('tools.splices'))}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label={t('tools.fibreLoss')} value={`${r.fibre} dB`} /><Stat label={t('tools.splitterLoss')} value={`${r.split} dB`} /><Stat label={t('tools.otherLoss')} value={`${r.other} dB`} />
        <Stat label={t('tools.expectedRx')} value={`${r.rx} dBm`} />
        <Stat label={t('tools.margin')} value={`${r.margin} dB`} tone={r.verdict === 'ok' ? 'text-emerald-600' : r.verdict === 'low' ? 'text-amber-600' : 'text-rose-600'} />
      </div>
      <div className={cn('rounded-xl px-3 py-2 text-sm font-semibold', r.verdict === 'ok' ? 'bg-emerald-500/10 text-emerald-700' : r.verdict === 'low' ? 'bg-amber-500/10 text-amber-700' : 'bg-rose-500/10 text-rose-700')}>
        {t(r.verdict === 'ok' ? 'tools.marginOk' : r.verdict === 'low' ? 'tools.marginLow' : 'tools.marginFail')}
      </div>
    </div>
  );
}

/* ───────────────────────── Destination monitoring ───────────────────────── */
const STATE_TONE: Record<string, string> = { up: 'bg-emerald-500', degraded: 'bg-amber-500', down: 'bg-rose-500', unknown: 'bg-slate-400' };
export function MonitorMenu() {
  const t = useT(); const user = useAuth((s) => s.user);
  const allowed = can(user, 'nmm.view', 'aaa.view', 'acs.view');
  const manage = can(user, 'nmm.manage', 'aaa.manage', 'admin.settings');
  const { data, refetch } = useGet<any[]>(allowed ? '/monitors' : null, undefined, { refetchInterval: 60_000 });
  const [open, setOpen] = useState(false); const [name, setName] = useState(''); const [host, setHost] = useState('');
  if (!allowed) return null;
  const rows = data ?? []; const bad = rows.filter((r) => r.state === 'down' || r.state === 'degraded').length;
  const add = async () => { try { await core.post('/monitors', { name, host }); setName(''); setHost(''); refetch(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id: string) => { await core.delete(`/monitors/${id}`).catch((e) => toast.error(apiError(e))); refetch(); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><span><IconBtn label={t('monitor.title')} badge={bad} active={open}><Radar className={cn('size-4', bad && 'text-rose-500')} /></IconBtn></span></PopoverTrigger>
      <PopoverContent align="end" className="w-[400px] p-0">
        <div className="flex items-center gap-2 border-b px-4 py-3"><Activity className="size-4 text-primary" /><span className="text-sm font-bold">{t('monitor.title')}</span><span className="ms-auto text-[11px] text-muted-foreground">{t('monitor.last3h')}</span></div>
        <div className="max-h-[50vh] space-y-1.5 overflow-y-auto p-2">
          {rows.map((r) => {
            const s = (r.samples ?? []) as { at: string; rtt: number | null; loss: number }[];
            const last = s[s.length - 1]; const maxR = Math.max(1, ...s.map((x) => Number(x.rtt ?? 0)));
            return (
              <div key={r.id} className="group rounded-xl border bg-card p-2.5">
                <div className="flex items-center gap-2">
                  <span className={cn('size-2.5 rounded-full', STATE_TONE[r.state], r.state === 'up' && 'animate-pulse')} />
                  <span className="text-sm font-semibold">{r.name}</span><span className="font-mono text-[11px] text-muted-foreground">{r.host}</span>
                  <span className="ms-auto text-[11px] font-semibold">{t(`monitor.${r.state}`)}</span>
                  {manage && <button onClick={() => remove(r.id)} className="hidden text-muted-foreground hover:text-rose-600 group-hover:block" title={t('monitor.remove')}><Trash2 className="size-3.5" /></button>}
                </div>
                <div className="mt-1.5 flex h-8 items-end gap-px">
                  {s.slice(-60).map((x, i) => <div key={i} className={cn('flex-1 rounded-sm', x.loss >= 100 ? 'bg-rose-500' : x.loss > 0 ? 'bg-amber-400' : 'bg-emerald-400')} style={{ height: `${x.loss >= 100 ? 100 : Math.max(12, (Number(x.rtt ?? 0) / maxR) * 100)}%` }} />)}
                </div>
                <div className="mt-1 flex text-[10.5px] text-muted-foreground"><span>{last?.rtt != null ? t('monitor.rtt', { ms: last.rtt }) : '—'} · {t('monitor.loss', { pct: last?.loss ?? 0 })}</span><span className="ms-auto">{t('monitor.since', { when: ago(r.state_since) })}</span></div>
              </div>
            );
          })}
          {!rows.length && <div className="p-6 text-center text-sm text-muted-foreground">{t('monitor.empty')}</div>}
        </div>
        {manage && <form className="flex gap-1.5 border-t p-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
          <input className={cn(field, 'h-8 text-xs')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('monitor.name')} required />
          <input className={cn(field, 'h-8 font-mono text-xs')} value={host} onChange={(e) => setHost(e.target.value)} placeholder={t('monitor.host')} required />
          <button className="flex h-8 shrink-0 items-center gap-1 rounded-lg bg-primary px-2.5 text-xs font-semibold text-white"><Plus className="size-3.5" />{t('common.add')}</button>
        </form>}
      </PopoverContent>
    </Popover>
  );
}

/* ───────────────────────── Notifications (live) ───────────────────────── */
const SEV: Record<string, string> = { critical: 'from-rose-500 to-red-600', warning: 'from-amber-400 to-orange-500', success: 'from-emerald-400 to-green-600', info: 'from-sky-400 to-blue-600' };
export function NotificationsMenu() {
  const t = useT(); const nav = useNavigate(); const qc = useQueryClient();
  const token = useAuth((s) => s.accessToken);
  const { data, refetch } = useGet<any>('/notifications', undefined, { refetchInterval: 120_000 });
  const [open, setOpen] = useState(false); const [live, setLive] = useState(false);
  const ctrl = useRef<AbortController | null>(null);
  // Live stream over fetch (keeps the bearer token in a header, not the URL).
  useEffect(() => {
    if (!token) return;
    const ac = new AbortController(); ctrl.current = ac;
    (async () => {
      try {
        const r = await fetch('/api/core/notifications/stream', { headers: { authorization: `Bearer ${token}` }, signal: ac.signal });
        if (!r.ok || !r.body) return;
        setLive(true);
        const rd = r.body.getReader(); const dec = new TextDecoder(); let buf = '';
        for (;;) {
          const { value, done } = await rd.read(); if (done) break;
          buf += dec.decode(value, { stream: true });
          const parts = buf.split('\n\n'); buf = parts.pop() ?? '';
          for (const p of parts) {
            const line = p.split('\n').find((l) => l.startsWith('data: '));
            if (!line) continue;
            const ev = JSON.parse(line.slice(6));
            toast(evTitle(t, ev), { description: dt(ev.created_at) });
            qc.invalidateQueries({ queryKey: ['/notifications'] }); refetch();
          }
        }
      } catch { /* closed */ } finally { setLive(false); }
    })();
    return () => ac.abort();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const items: any[] = data?.items ?? []; const readAt = data?.readAt ? new Date(data.readAt) : new Date(0);
  const markRead = async () => { await core.post('/notifications/read', {}); refetch(); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><span><IconBtn label={t('notifications.title')} badge={data?.unread} active={open}><Bell className="size-4" /></IconBtn></span></PopoverTrigger>
      <PopoverContent align="end" className="w-[400px] p-0">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <span className="text-sm font-bold">{t('notifications.title')}</span>
          {live && <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-700"><span className="size-1.5 animate-pulse rounded-full bg-emerald-500" />{t('notifications.live')}</span>}
          {!!data?.unread && <button onClick={markRead} className="ms-auto flex items-center gap-1 text-xs font-semibold text-primary"><CheckCheck className="size-3.5" />{t('notifications.markRead')}</button>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {items.map((n) => (
            <button key={n.id} onClick={() => { if (n.href) { setOpen(false); nav(n.href); } }} className={cn('flex w-full items-start gap-3 rounded-xl px-2.5 py-2 text-start hover:bg-muted', new Date(n.created_at) > readAt && 'bg-primary/5')}>
              <span className={cn('mt-0.5 size-2.5 shrink-0 rounded-full bg-gradient-to-br', SEV[n.severity])} />
              <span className="min-w-0 flex-1"><span className="block text-[13px] font-medium leading-snug">{evTitle(t, n)}</span><span className="text-[11px] text-muted-foreground">{ago(n.created_at)} · {n.source}</span></span>
            </button>
          ))}
          {!items.length && <div className="p-8 text-center text-sm text-muted-foreground">{t('notifications.empty')}</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
